/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';

import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter as rxFilter, takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {Report} from '../report';
import {FilterField} from '../reports.interfaces';
import {ReportsService} from '../reports.service';

@Component({
  selector: 'gp-report-filter',
  templateUrl: './report-filter.component.html',
  styleUrls: ['./report-filter.component.scss']
})

export class ReportFilterComponent extends Destroyable implements OnChanges, OnInit {

  @Input() public report: Report;
  @Input() public enabledFields: string[];
  @Input() public toggleable = true;
  @Input() public isOpen = false;
  @Input() public showAndOperatorText = true;
  @Input() public sortable = true;
  @Output() public allFiltersLoaded: EventEmitter<void> = new EventEmitter<void>();
  public filterItemsLoaded = 0;
  public totalFilterItems = 0;
  public dragFilter: FilterField;
  public isDragEnabled = false;
  private dropTarget: Subject<number> = new Subject<number>();

  constructor(private reportsService: ReportsService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    this.dropTarget
      .pipe(
        distinctUntilChanged(),
        debounceTime(100),
        rxFilter(() => !!this.dragFilter),
        takeUntil(this.componentDestroyed$),
      )
      .subscribe((targetIndex: number) => this.moveDraggedFilter(targetIndex));
  }

  public setDragEnabled(enabled: boolean): void {
    this.isDragEnabled = this.sortable && enabled;
  }

  public dragStart(filterField: FilterField): void {
    this.dragFilter = filterField;
  }

  public dragEnter(index: number): void {
    this.dropTarget.next(index);
  }

  public dragEnd(): void {
    this.dragFilter = null;
    this.isDragEnabled = false;
  }

  private moveDraggedFilter(targetIndex: number): void {
    const currentIndex = this.report.filters.indexOf(this.dragFilter);
    if (currentIndex === -1 || currentIndex === targetIndex) {
      return;
    }
    this.report.filters.splice(currentIndex, 1);
    this.report.filters.splice(targetIndex, 0, this.dragFilter);
    this.report.filters.forEach((filterField: FilterField, index: number) => filterField.fieldPosition = index);
    this.reportsService.$reportUpdated.next(this.report);
    this.reportsService.markAsChanged();
    this.cd.markForCheck();
  }

  public removeFilter(filterId: string): void {
    const index = this.report.filters.findIndex((filterField: FilterField) => filterField.field === filterId);
    if (index !== -1) {
      this.report.filters.splice(index, 1);
      this.reportsService.$reportUpdated.next(this.report);
      this.reportsService.markAsChanged();
    }
  }

  public ngOnChanges(): void {
    this.totalFilterItems = this.report?.filters?.length || 0;
    this.filterItemsLoaded = 0;
  }

  public onFilterItemLoaded() {
    this.filterItemsLoaded++;
    if (this.filterItemsLoaded >= this.totalFilterItems) {
      this.reportsService.$reportUpdated.next(this.report);
      this.allFiltersLoaded.emit();
    }
  }

}
